import { useContext, useEffect, useMemo } from 'react';

import Radio from '@react/react-spectrum/Radio';
import RadioGroup from '@react/react-spectrum/RadioGroup';
import { ComboBox, Item } from '@adobe/react-spectrum';
import Wait from '@react/react-spectrum/Wait';

import { QuickSetupContext } from './Context';
import { useMyWorkspaces } from 'data/hooks';
import { getWorkspaceOption } from './livedemos';

export default function WorkspaceParameters() {
  const { context, setContextValue } = useContext(QuickSetupContext);

  const workspaceOption = useMemo(
    () => getWorkspaceOption(context),
    [context.workspaceOption, context.workspaceId]
  );

  const setWorkspaceOption = (value) => {
    setContextValue({ path: 'workspaceOption', value });

    if (value === 'new') {
      setContextValue({ path: 'workspaceId', value: null });
      setContextValue({ path: 'existingLivedemosWorkspaceId', value: null });
    } else if (value === 'none') {
      setContextValue({ path: 'workspaceId', value: -1 });
      setContextValue({ path: 'existingLivedemosWorkspaceId', value: null });
    }
  };

  return (
    <div>
      <RadioGroup
        name="workspace-option"
        selectedValue={workspaceOption}
        onChange={setWorkspaceOption}>
        <Radio label="Create new workspace" value="new" />
        <Radio label="Use existing workspace" value="existing" />
        <Radio label="Don't use workspace" value="none" />
      </RadioGroup>

      {workspaceOption === 'new' && (
        <div className="field-description">
          <em>
            New workspace will be created and all newly created projects and
            environments will be added to it.
          </em>
        </div>
      )}

      {workspaceOption === 'existing' && (
        <div>
          <WorkspaceSelect />
          <div className="field-description">
            <em>
              Newly created projects and environments will be added to this
              workspace.
            </em>
          </div>
        </div>
      )}

      {/* empty div to make the spacing consistent */}
      {workspaceOption === 'none' && <div className="field-description" />}
    </div>
  );
}

function WorkspaceSelect() {
  const { context, setContextValue } = useContext(QuickSetupContext);
  const { loading, error, data } = useMyWorkspaces();

  const options = useMemo(() => {
    if (data) {
      const workspaces = [
        ...(data?.ownedWorkspaces || []),
        ...(data?.sharedWorkspaces || [])
      ].sort((a, b) => a?.name?.localeCompare(b?.name));

      return workspaces.map((workspace) => ({
        label: workspace.name,
        value: String(workspace.id),
        livedemosWorkspaceId: workspace.livedemosWorkspaceId || null
      }));
    } else {
      return [];
    }
  }, [data]);

  const searchValue = context.workspaceSearch || '';
  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchValue.toLowerCase())
  );

  // Hydrate livedemos link when workspaceId comes from a ?workspace= deep link.
  // Only sets the label when it is not already populated, so user edits are not overwritten.
  useEffect(() => {
    if (!data || context.workspaceId == null || context.workspaceId === -1) {
      return;
    }
    const selected = options.find(
      (opt) => opt.value === String(context.workspaceId)
    );
    if (!selected) {
      return;
    }
    if (!context.workspaceSearch) {
      setContextValue({ path: 'workspaceSearch', value: selected.label });
    }
    const livedemosId = selected.livedemosWorkspaceId || null;
    if (context.existingLivedemosWorkspaceId !== livedemosId) {
      setContextValue({
        path: 'existingLivedemosWorkspaceId',
        value: livedemosId
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, context.workspaceId, options]);

  if (loading) {
    return <Wait size="S" />;
  }

  if (error) {
    return <span>{error.message}</span>;
  }

  return (
    <ComboBox
      label="Workspace"
      selectedKey={
        context.workspaceId !== null && context.workspaceId !== undefined
          ? String(context.workspaceId)
          : null
      }
      onSelectionChange={(key) => {
        setContextValue({ path: 'workspaceId', value: key });
        const selected = options.find((opt) => opt.value === key);
        if (selected) {
          setContextValue({ path: 'workspaceSearch', value: selected.label });
        }
        setContextValue({
          path: 'existingLivedemosWorkspaceId',
          value: selected?.livedemosWorkspaceId || null
        });
      }}
      items={filteredOptions}
      inputValue={searchValue}
      onInputChange={(value) => {
        setContextValue({ path: 'workspaceSearch', value });
      }}>
      {(item) => <Item key={item.value}>{item.label}</Item>}
    </ComboBox>
  );
}
